import { useEffect, useState } from 'react';
import { gameConfigAdminApi, type GameConfigData } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/translations';

// ─────────────────────────────────────────────────────────────────────────────
// Página de Configuração do Jogo — /admin/gameconfig
// ─────────────────────────────────────────────────────────────────────────────

type ConfigFieldDef = {
  key: keyof Omit<GameConfigData, 'updatedBy' | 'updatedAt'>;
  labelKey: TranslationKey;
  hintKey: TranslationKey;
  step: number;
  min: number;
  max?: number;
};

const CONFIG_FIELDS: ConfigFieldDef[] = [
  { key: 'difficultyMultiplier', labelKey: 'gameconfig.difficultyMultiplier.label', hintKey: 'gameconfig.difficultyMultiplier.hint', step: 0.1, min: 0.1 },
  { key: 'monsterXpRate', labelKey: 'gameconfig.monsterXpRate.label', hintKey: 'gameconfig.monsterXpRate.hint', step: 0.1, min: 0 },
  { key: 'pointsPerLevel', labelKey: 'gameconfig.pointsPerLevel.label', hintKey: 'gameconfig.pointsPerLevel.hint', step: 1, min: 1 },
  { key: 'xpDecayRate', labelKey: 'gameconfig.xpDecayRate.label', hintKey: 'gameconfig.xpDecayRate.hint', step: 0.05, min: 0 },
  { key: 'maxLevel', labelKey: 'gameconfig.maxLevel.label', hintKey: 'gameconfig.maxLevel.hint', step: 1, min: 1, max: 200 },
  { key: 'levelDelta', labelKey: 'gameconfig.levelDelta.label', hintKey: 'gameconfig.levelDelta.hint', step: 1, min: 0, max: 10 },
  { key: 'powerPerExtraPlayer', labelKey: 'gameconfig.powerPerExtraPlayer.label', hintKey: 'gameconfig.powerPerExtraPlayer.hint', step: 0.1, min: 0, max: 5 },
  { key: 'bossSpawnThreshold', labelKey: 'gameconfig.bossSpawnThreshold.label', hintKey: 'gameconfig.bossSpawnThreshold.hint', step: 1, min: 1, max: 10 },
  { key: 'maxEnergy', labelKey: 'gameconfig.maxEnergy.label', hintKey: 'gameconfig.maxEnergy.hint', step: 1, min: 10, max: 1000 },
  { key: 'energyRechargeRate', labelKey: 'gameconfig.energyRechargeRate.label', hintKey: 'gameconfig.energyRechargeRate.hint', step: 1, min: 1 },
  { key: 'battleEnergyCost', labelKey: 'gameconfig.battleEnergyCost.label', hintKey: 'gameconfig.battleEnergyCost.hint', step: 1, min: 0 },
];

export function GameConfigPage() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<GameConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    gameConfigAdminApi.getConfig()
      .then(setConfig)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key: keyof GameConfigData, value: number) => {
    setConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await gameConfigAdminApi.updateConfig(config);
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('gameconfig.title')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  if (!config) return (
    <>
      <div className="adminHeader"><h1>{t('gameconfig.title')}</h1></div>
      <div className="adminContent">
        <div className="alertError">{error || t('gameconfig.error')}</div>
      </div>
    </>
  );

  return (
    <>
      <div className="adminHeader">
        <h1>{t('gameconfig.title')}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && (
            <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>
              {t('gameconfig.saved')}
            </span>
          )}
          <button className="btn btnPrimary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? t('gameconfig.btn.saving') : t('gameconfig.btn.save')}
          </button>
        </div>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <div className="alertInfo" style={{
          background: 'rgba(68,136,255,0.08)',
          border: '1px solid rgba(68,136,255,0.25)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 24,
          fontSize: 13,
          color: 'var(--text-dim)',
        }}>
          <b>{t('gameconfig.info.title')}</b> <br/>
          {t('gameconfig.info.desc')}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
          <div className="adminCard">
            <div className="adminCardHeader">{t('gameconfig.card.rules')}</div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {CONFIG_FIELDS.map(({ key, labelKey, hintKey, step, min, max }) => (
                <div key={key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <label className="formLabel" style={{ margin: 0, flex: 1 }}>{t(labelKey)}</label>
                    <input
                      type="number"
                      className="formInput"
                      style={{ width: 120, textAlign: 'right' }}
                      value={config[key] as number}
                      step={step}
                      min={min}
                      max={max}
                      onChange={(e) => handleChange(key, parseFloat(e.target.value) || min)}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, paddingLeft: 2 }}>{t(hintKey)}</p>
                </div>
              ))}
              
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <label className="formLabel" style={{ margin: 0, flex: 1 }}>{t('gameconfig.antiCarryMode.label')}</label>
                  <select
                    className="formInput"
                    style={{ width: 120, textAlign: 'right' }}
                    value={config.antiCarryMode}
                    onChange={(e) => handleChange('antiCarryMode' as any, e.target.value as any)}
                  >
                    <option value="average">Average</option>
                    <option value="max_level">Max Level</option>
                  </select>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, paddingLeft: 2 }}>{t('gameconfig.antiCarryMode.hint')}</p>
              </div>

            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {config.updatedBy && (
              <div className="adminCard">
                <div className="adminCardHeader">{t('gameconfig.card.history')}</div>
                <div style={{ padding: '12px 18px', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6 }}>
                  <div>{t('gameconfig.history.updatedBy')}</div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>{config.updatedBy}</div>
                  <div style={{ marginTop: 4 }}>
                    {new Date(config.updatedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
